import { useEffect, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

/**
 * What the browser will let this operator do on an emergency call, asked of the
 * browser rather than of the API.
 *
 * `no-hardware` is deliberately distinct from `denied`: a refusal is fixed at
 * site settings, a missing device is fixed with a headset, and sending an
 * operator to the wrong one of those is the failure this separation avoids.
 */
export type MicPermissionState = 'granted' | 'prompt' | 'denied' | 'no-hardware'

/**
 * Options for useMicPermission hook
 */
export interface UseMicPermissionIProps {
  /**
   * Whether the browser should be asked at all. The banner calls this before
   * it knows whether it will render, so without the switch the hook probes
   * microphone permissions on every pageload even with emergency calling off.
   * Defaults to `true` so existing call sites are unchanged.
   */
  isEnabled?: boolean
}

/**
 * Return type for useMicPermission hook
 */
export interface UseMicPermissionReturnIProps {
  micState: MicPermissionState | null
  isChecking: boolean
  requestPermission: () => Promise<void>
}

/**
 * Only an actual refusal earns the "your browser is blocking it" panel, whose
 * instruction is site settings. Everything else is a machine problem the
 * operator fixes with a headset: no device, a device another application is
 * holding (Zoom, Teams), or a browser with no audio stack.
 */
const DENIALS = new Set(['NotAllowedError', 'SecurityError'])

const micStateFromError = (error: unknown): 'denied' | 'no-hardware' => {
  const name = error instanceof Error ? error.name : ''
  return DENIALS.has(name) ? 'denied' : 'no-hardware'
}

/**
 * The operator declining the browser prompt, which is a decision rather than
 * a fault. `SecurityError` is deliberately not included: it also produces the
 * blocked panel, but it means the page is running outside a secure context.
 */
const isUserRefusal = (error: unknown): boolean =>
  error instanceof Error && error.name === 'NotAllowedError'

/**
 * Safari does not accept `microphone` as a `PermissionName` and rejects the
 * query with a `TypeError`. That is a browser capability, not a fault, and it
 * happens on every Safari pageload with the feature on — reporting it would
 * bury the query-path failures that are genuinely wrong.
 */
const isUnsupportedPermissionName = (error: unknown): boolean =>
  error instanceof TypeError

/**
 * Hook to check and request microphone permissions.
 * Returns null initially while checking, then a state once determined.
 *
 * `prompt` is a first-class state rather than being folded into "fine": it is
 * the only one in which `getUserMedia` can still raise the browser permission
 * dialog, so it is what makes the banner's call-to-action able to do anything
 * at all. A blocked origin reports `denied` and the dialog is suppressed
 * (ENG-32960).
 */
export const useMicPermission = ({
  isEnabled = true,
}: UseMicPermissionIProps = {}): UseMicPermissionReturnIProps => {
  const [micState, setMicState] = useState<MicPermissionState | null>(null)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    // Disabled stays indistinguishable from "still checking": `micState` is
    // null and `isChecking` true, so no consumer can read a disabled hook as a
    // settled answer. The reset matters on the second disable — holding the
    // previously settled state would let the banner render one frame of a
    // stale answer, and fire its view event, before the new check resolves.
    if (!isEnabled) {
      setMicState(null)
      setIsChecking(true)
      return
    }

    let permissionStatus: PermissionStatus | null = null
    // `navigator.permissions.query` can resolve after this run is torn down.
    // Without the flag the continuation would still set state and install an
    // `onchange` handler that the already-completed cleanup cannot remove.
    let isActive = true

    const checkPermission = async () => {
      try {
        setIsChecking(true)

        // Check if the Permissions API is available
        if (!navigator.permissions) {
          // Fallback: assume permission is granted if API not available
          setMicState('granted')
          setIsChecking(false)
          return
        }

        // Query microphone permission status
        const status = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        })

        if (!isActive) {
          return
        }

        permissionStatus = status
        setMicState(status.state)

        // Listen for permission changes
        status.onchange = () => {
          setMicState(status.state)
        }
      } catch (err) {
        // Assume permission is granted if we can't check
        if (!isUnsupportedPermissionName(err)) {
          Sentry.captureException(err, {
            tags: { action: 'query', feature: 'mic_permission' },
          })
        }

        if (isActive) {
          setMicState('granted')
        }
      } finally {
        if (isActive) {
          setIsChecking(false)
        }
      }
    }

    checkPermission()

    // Cleanup: stop this run and remove its permission change listener
    return () => {
      isActive = false

      if (permissionStatus) {
        permissionStatus.onchange = null
      }
    }
  }, [isEnabled])

  const requestPermission = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Stop all tracks immediately after getting permission. The grant was the
      // point; holding the stream would light the OS recording indicator.
      stream.getTracks().forEach((track) => track.stop())
      setMicState('granted')
    } catch (err) {
      // A refused prompt is the operator making a choice, not the app
      // failing, and it is the most likely outcome of pressing the button.
      // Reporting it would bury the cases that are genuinely wrong: a machine
      // with no usable microphone, or a SecurityError from a non-secure
      // context, which also reads as `denied` but is a deployment fault.
      if (!isUserRefusal(err)) {
        Sentry.captureException(err, {
          tags: { action: 'request', feature: 'mic_permission' },
        })
      }

      setMicState(micStateFromError(err))
    }
  }

  return {
    micState,
    isChecking,
    requestPermission,
  }
}
